import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Button, Form, Spinner } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';

import ConsumerProtectionInsurance from '~/components/ConsumerProtectionInsurance';
import Liability from '~/components/Liability';
import Stampduty from '~/components/Stampduty';
import UseOfContractors from '~/components/UseOfContractors';
import Modal from '~/components-ds/Modal';
import { ANZSIC_TO_CPI_TYPES } from '~/constants/domain-specific';
import ERRORS from '~/constants/errors';
import {
  useAppContent,
  useAppDispatch,
  useBusinessDetails,
  useCoverDetails,
  useSelectCover,
} from '~/hooks';
import { useCoverDetailsEffects } from '~/hooks/useCoverDetailsEffects';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { selectIsValidLiability, selectIsValidLiabilityCpi } from '~/selectors/selectCoverValidity';
import { updateLiabilityLimit, updateUseOfContractors } from '~/slices/coverDetails';
import { hardDecline } from '~/slices/decline';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction, qalTrackEngagementAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { States } from '~/types/enums';
import { isTruthy } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { logError } from '../../utils/logError';
import { LiabilityForm } from './types';

const CoverDetailsLiability: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsBusinessLiability',
  });

  const isPropertyOwner = useSelector(selectIsPropertyOwner);
  const isValidLiabilityCpi = useSelector(selectIsValidLiabilityCpi);
  const isValidLiability = useSelector(selectIsValidLiability);
  const dispatch = useAppDispatch();
  const { occupation: _occupation } = useBusinessDetails();
  const { anzsicCode } = _occupation ?? {};
  const { stampdutyExempted, useOfContractors, liabilityLimit } = useCoverDetails();
  const { industry, fullBusinessAddress, state } = useBusinessDetails();
  const { validatedCovers } = useSelectCover();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.Liability,
    onSuccess: () => setIsModalOpen(false),
  });
  const estCostOfContractors = industry?.limits?.estCostOfContractors;
  const isNswAddress = fullBusinessAddress?.stateCode === States.NSW || state === States.NSW;
  const isCPI = anzsicCode! in ANZSIC_TO_CPI_TYPES;
  const form = useFormWithAnalytics<LiabilityForm>({
    mode: 'onChange',
    defaultValues: {
      liabilityLimit: liabilityLimit ?? undefined,
      useOfContractors: undefined,
    },
    ...(liabilityLimit != null &&
      useOfContractors != null && {
        values: {
          liabilityLimit,
          useOfContractors,
        },
      }),
  });

  const stampDutyNextButtonDisabled = !validatedCovers.includes(Cover.Liability);

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  const onCloseHandler = () => {
    setIsModalOpen(false);
    dispatch(
      qalTrackEngagementAction({
        eventAction: 'modal: close',
        eventLabel: 'nsw stamp duty',
        data: {},
      })
    );
  };

  useEffect(() => {
    if (isValidLiability && (!isNswAddress || isTruthy(stampdutyExempted))) {
      dispatch(addValidatedCover(Cover.Liability));
    } else {
      dispatch(removeValidatedCover(Cover.Liability));
    }
  }, [isValidLiability, isNswAddress, stampdutyExempted, dispatch]);

  const handleNext = () => {
    dispatch(addValidatedCover(Cover.Liability));
    quotePremiums().catch(logError);
  };

  const doSubmit = form.handleSubmit((values) => {
    dispatch(updateLiabilityLimit(+values.liabilityLimit));
    dispatch(updateUseOfContractors(values.useOfContractors));
    handleFormComplete();
    const isValidUseOfContractors =
      values.useOfContractors !== undefined && estCostOfContractors !== undefined
        ? values.useOfContractors <= estCostOfContractors
        : false;
    if (!isPropertyOwner && !isValidUseOfContractors) {
      dispatch(hardDecline(ERRORS.useOfContractors));
      return;
    }
    if (isNswAddress) setIsModalOpen(true);
    else handleNext();
  });

  return (
    <>
      <Spinner
        loading={isLoading}
        description={getSharedContent('loadingLabel')}
        onClose={() => {}}
      />
      <FormCard title={getTextContent('title')} variant="sectionTitle">
        <FormProvider {...form}>
          <Form onSubmit={doSubmit}>
            <Liability />
            {!isPropertyOwner && <UseOfContractors />}
            {isCPI && <ConsumerProtectionInsurance anzicCode={anzsicCode!} />}
            <FormBlock preset="button">
              <Button
                type="submit"
                data-track={`${buttonText} - liability`}
                disabled={!form.formState.isValid || !isValidLiabilityCpi || isLoading}
                label={buttonText}
              />
            </FormBlock>
          </Form>
        </FormProvider>
      </FormCard>

      {isModalOpen && (
        <Modal
          contentMaxHeight="350px"
          open
          onClose={onCloseHandler}
          id="stamp-duty-modal"
          title={getTextContent('fields.stampDutyModal.title')}
          primaryBtnProps={{
            label: buttonText,
            onClick: handleNext,
            'data-track': `${buttonText} - stampduty`,
            disabled: isLoading || stampDutyNextButtonDisabled,
          }}
          // Stops maximum call stack exceed when clicking on background due to nested modals
          disableEnforceFocus={isLoading}
        >
          <Stampduty />
        </Modal>
      )}
    </>
  );
};

export default CoverDetailsLiability;
